/** State and actions behind the Home tab's "Download PDF": preview first, then save. */

import { useCallback, useState } from "react";
import type { HistorySnapshot, RosterGridData, RosterState } from "@/types/api";
import { renderExportImages, writeRosterPdf, type ExportData } from "@/components/tabs/home/pdfExport";

interface PdfExportOptions {
  gridData: RosterGridData | null;
  state: RosterState | null;
  displayedSnapshot: HistorySnapshot | null | undefined;
  sessionId: string;
}

export function usePdfExport({ gridData, state, displayedSnapshot, sessionId }: PdfExportOptions) {
  // Export preview modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState<ExportData | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const handleExport = useCallback(() => {
    if (!gridData || !state || !displayedSnapshot) return;
    setExportData(renderExportImages(gridData, state, displayedSnapshot));
    setShowExportModal(true);
  }, [gridData, displayedSnapshot, state]);

  const operationMode = state?.operation_mode;
  const handleDownloadPdf = useCallback(async () => {
    if (!exportData) return;
    setPdfDownloading(true);
    // Let "Generating…" paint before the (synchronous, heavy) PDF build starts.
    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    try {
      await writeRosterPdf(exportData, sessionId, operationMode);
    } finally {
      setPdfDownloading(false);
    }
  }, [exportData, sessionId, operationMode]);

  return {
    showExportModal, setShowExportModal, exportData, pdfDownloading, handleExport, handleDownloadPdf,
  };
}
